import { Notifications330FilterModel } from "./Notifications330FilterModel";

export const NOTIFICATIONS330_FILTER_KEY = "monitorDashboard.notifications330Filter";

export const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";

export const NOTIFICATIONS330_DASHBOARD_ROUTE = {
    name: "MonitorDashboard",
    query: {
        topTab: "1",
    },
};

export const NOTIFICATIONS330_DETAILS_ROUTES = {
    timeline: "ComplaintsTimelineDetails",
    statuses: "ComplaintsStatusesDetails",
    frequency: "ComplaintFrequencyPerSenderDetails",
};

export const NOTIFICATIONS330_DETAILS_SELECT_MENU_PROPS: Record<string, unknown> = {
    contentClass: "monitor-dashboard-select-menu all-requests-details-client-menu",
    offsetY: true,
    maxHeight: 260,
};

export interface Notifications330StoredFilterModel {
    dateFrom: string;
    dateTo: string;
    tenantId: string;
    providerId: string;
    customerType: number;
    senderName?: string;
    complaintStatus?: number;
    timelineYear?: number;
    timelineMonth?: number;
}

export interface Notifications330DetailsPaginationState {
    page: number;
    rowsPerPage: number;
    sortBy: string;
    descending: boolean;
}

export interface Notifications330DetailsLocalFilters {
    senderName?: string;
    clientName?: string;
    complaintStatus?: number | null;
    complaintFrequency?: string | number | null;
    timelineYear?: number | null;
    timelineMonth?: number | null;
}

export interface Notifications330DetailsDrillDownState {
    senderName?: string;
    complaintStatus?: number | null;
    timelineYear?: number | null;
    timelineMonth?: number | null;
}

export interface Notifications330DetailsRestoreResult {
    parentFilter: Notifications330FilterModel | null;
    drillDown: Notifications330DetailsDrillDownState;
}

export interface Notifications330CustomerTypeLabels {
    government: string;
    charity: string;
    privateLabel: string;
}

export interface Notifications330ComplaintStatusLabels {
    pendingForReview: string;
    deactivated: string;
    falseComplaint: string;
}

export function getTodayStr(): string {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return yyyy + "-" + mm + "-" + dd;
}

export function createEmptyNotifications330StoredFilter(): Notifications330StoredFilterModel {
    return {
        dateFrom: "",
        dateTo: "",
        tenantId: EMPTY_GUID,
        providerId: EMPTY_GUID,
        customerType: 0,
    };
}

export function createDefaultNotifications330DetailsPagination(): Notifications330DetailsPaginationState {
    return {
        page: 1,
        rowsPerPage: 10,
        sortBy: "createdDate",
        descending: true,
    };
}

export function resetNotifications330DetailsPagination(
    pagination: Notifications330DetailsPaginationState,
): void {
    pagination.page = 1;
    pagination.rowsPerPage = 10;
    pagination.sortBy = "createdDate";
    pagination.descending = true;
}

export function loadNotifications330FilterFromStorage(
    target: Notifications330StoredFilterModel,
): void {
    try {
        const raw = sessionStorage.getItem(NOTIFICATIONS330_FILTER_KEY);
        if (!raw) {
            return;
        }
        const parsed = JSON.parse(raw);
        if (parsed && parsed.dateFrom) {
            target.dateFrom = parsed.dateFrom;
        }
        if (parsed && parsed.dateTo) {
            target.dateTo = parsed.dateTo;
        }
        if (parsed && parsed.tenantId) {
            target.tenantId = parsed.tenantId;
        } else if (parsed && parsed.tenant) {
            target.tenantId = parsed.tenant;
        }
        if (parsed && parsed.providerId) {
            target.providerId = parsed.providerId;
        }
        if (parsed && parsed.customerType != null) {
            target.customerType = Number(parsed.customerType);
        }
        if (parsed && parsed.senderName) {
            target.senderName = String(parsed.senderName);
        }
        if (parsed && parsed.complaintStatus != null) {
            target.complaintStatus = Number(parsed.complaintStatus);
        }
        if (parsed && parsed.timelineYear != null) {
            target.timelineYear = Number(parsed.timelineYear);
        }
        if (parsed && parsed.timelineMonth != null) {
            target.timelineMonth = Number(parsed.timelineMonth);
        }
    } catch (e) {
    }
}

export function saveNotifications330FilterToStorage(
    filter: Notifications330FilterModel,
    senderName?: string,
    timelineYear?: number,
    timelineMonth?: number,
    complaintStatus?: number,
): void {
    const payload: Notifications330StoredFilterModel = {
        dateFrom: filter.dateFrom,
        dateTo: filter.dateTo,
        tenantId: filter.tenant,
        providerId: filter.providerId,
        customerType: filter.customerType != null ? filter.customerType : 0,
    };
    if (senderName) {
        payload.senderName = senderName;
    }
    if (complaintStatus != null && complaintStatus > 0) {
        payload.complaintStatus = complaintStatus;
    }
    if (timelineYear != null && timelineMonth != null) {
        payload.timelineYear = timelineYear;
        payload.timelineMonth = timelineMonth;
    } else {
        delete payload.timelineYear;
        delete payload.timelineMonth;
    }
    try {
        sessionStorage.setItem(NOTIFICATIONS330_FILTER_KEY, JSON.stringify(payload));
    } catch (e) {
    }
}

export function restoreNotifications330DetailsContext(
    seed?: Partial<Notifications330StoredFilterModel>,
): Notifications330DetailsRestoreResult {
    const stored = createEmptyNotifications330StoredFilter();
    if (seed) {
        if (seed.dateFrom) {
            stored.dateFrom = seed.dateFrom;
        }
        if (seed.dateTo) {
            stored.dateTo = seed.dateTo;
        }
        if (seed.tenantId) {
            stored.tenantId = seed.tenantId;
        }
        if (seed.providerId) {
            stored.providerId = seed.providerId;
        }
        if (seed.customerType != null) {
            stored.customerType = seed.customerType;
        }
    }
    loadNotifications330FilterFromStorage(stored);
    if (!stored.dateFrom) {
        return {
            parentFilter: null,
            drillDown: {},
        };
    }

    const drillDown: Notifications330DetailsDrillDownState = {};
    if (stored.senderName) {
        drillDown.senderName = stored.senderName;
    }
    if (stored.complaintStatus != null && stored.complaintStatus > 0) {
        drillDown.complaintStatus = Number(stored.complaintStatus);
    }
    if (stored.timelineYear != null && stored.timelineMonth != null) {
        drillDown.timelineYear = Number(stored.timelineYear);
        drillDown.timelineMonth = Number(stored.timelineMonth);
    }

    return {
        parentFilter: toNotifications330FilterModel(stored),
        drillDown: drillDown,
    };
}

export function toNotifications330FilterModel(
    stored: Notifications330StoredFilterModel,
): Notifications330FilterModel {
    return {
        dateFrom: stored.dateFrom,
        dateTo: stored.dateTo || getTodayStr(),
        tenant: stored.tenantId || EMPTY_GUID,
        providerId: stored.providerId || EMPTY_GUID,
        customerType: stored.customerType != null ? stored.customerType : 0,
    };
}

export function buildNotifications330ScopePayload(filter: Notifications330FilterModel): any {
    return {
        dateFrom: filter.dateFrom,
        dateTo: filter.dateTo || getTodayStr(),
        tenantId: filter.tenant,
        providerId: filter.providerId,
        customerType: filter.customerType || 0,
    };
}

export function parseComplaintFrequencyFilter(
    value: string | number | null | undefined,
): number | null {
    if (value == null) {
        return null;
    }
    const freqText = String(value).trim();
    if (freqText.length === 0) {
        return null;
    }
    const parsed = Number(freqText);
    if (isNaN(parsed)) {
        return null;
    }
    return parsed;
}

export function buildNotifications330DetailsPayload(
    parentFilter: Notifications330FilterModel,
    pagination: Notifications330DetailsPaginationState,
    localFilters: Notifications330DetailsLocalFilters,
): any {
    const payload: any = buildNotifications330ScopePayload(parentFilter);
    payload.senderName = localFilters.senderName ? String(localFilters.senderName).trim() : "";
    payload.clientName = localFilters.clientName ? String(localFilters.clientName).trim() : "";
    payload.pageIndex = (pagination.page - 1) >= 0 ? pagination.page - 1 : 0;
    payload.pageSize = pagination.rowsPerPage || 10;
    payload.sortBy = pagination.sortBy || "createdDate";
    payload.sortDirection = pagination.descending ? "desc" : "asc";

    if (localFilters.timelineYear != null && localFilters.timelineMonth != null) {
        payload.timelineYear = localFilters.timelineYear;
        payload.timelineMonth = localFilters.timelineMonth;
    }

    if (localFilters.complaintStatus != null && localFilters.complaintStatus > 0) {
        payload.complaintStatus = localFilters.complaintStatus;
    }

    const complaintFrequency = parseComplaintFrequencyFilter(localFilters.complaintFrequency);
    if (complaintFrequency != null) {
        payload.complaintFrequency = complaintFrequency;
    }

    return payload;
}

export function formatCreatedDate(value: string): string {
    if (!value) {
        return "";
    }
    const s = String(value);
    if (s.length >= 10) {
        return s.substring(0, 10);
    }
    return s;
}

export function formatNotifications330CustomerType(
    value: number | null,
    labels: Notifications330CustomerTypeLabels,
): string {
    if (value == null) {
        return "";
    }
    switch (Number(value)) {
        case 1:
            return labels.government;
        case 2:
            return labels.charity;
        case 3:
            return labels.privateLabel;
        default:
            return String(value);
    }
}

export function formatNotifications330ComplaintStatus(
    value: number | null,
    labels: Notifications330ComplaintStatusLabels,
): string {
    if (value == null) {
        return "";
    }
    switch (Number(value)) {
        case 1:
            return labels.pendingForReview;
        case 2:
            return labels.deactivated;
        case 3:
            return labels.falseComplaint;
        default:
            return String(value);
    }
}

export function buildNotifications330ComplaintStatusOptions(
    labels: Notifications330ComplaintStatusLabels,
): Array<{ value: number; text: string }> {
    return [
        {
            value: 1,
            text: labels.pendingForReview,
        },
        {
            value: 2,
            text: labels.deactivated,
        },
        {
            value: 3,
            text: labels.falseComplaint,
        },
    ];
}

export function assignComplaintFrequencyRowKeys(rows: any[]): any[] {
    const result: any[] = [];
    for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        row.rowKey =
            String(row.createdDate) +
            "-" +
            String(row.senderName) +
            "-" +
            String(row.clientName) +
            "-" +
            String(i);
        result.push(row);
    }
    return result;
}

export function assignComplaintsTimelineRowKeys(rows: any[]): any[] {
    const result: any[] = [];
    for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        row.rowKey =
            String(row.createdDate) +
            "-" +
            String(row.senderName) +
            "-" +
            String(row.clientName) +
            "-" +
            String(row.smsServiceProvider) +
            "-" +
            String(i);
        result.push(row);
    }
    return result;
}

export function assignComplaintsStatusesRowKeys(rows: any[]): any[] {
    const result: any[] = [];
    for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        row.rowKey =
            String(row.createdDate) +
            "-" +
            String(row.senderName) +
            "-" +
            String(row.clientName) +
            "-" +
            String(row.complaintStatus) +
            "-" +
            String(i);
        result.push(row);
    }
    return result;
}

export function downloadExcelFromBase64(base64: string, fileName: string): void {
    try {
        const binary = atob(base64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
            bytes[i] = binary.charCodeAt(i);
        }
        const blob = new Blob([bytes], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    } catch (e) {
    }
}
